import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../utils/apiClient';
import logger from '../utils/logger';
import { useWordContext } from '../context/WordContext';
import './ChatbotDictionary.css';

// Extract the specific sentence containing the target word
const extractSentenceContext = (fullText, targetWord) => {
  try {
    if (!fullText || !targetWord) return fullText;
    
    const targetIndex = fullText.indexOf(targetWord);
    
    if (targetIndex === -1) return fullText;
    
    // Find sentence boundaries
    // Start by finding the beginning of the sentence
    let startIndex = targetIndex;
    while (startIndex > 0) {
      // Look for sentence-ending punctuation or start of text
      // For Chinese, include 。、！？
      if (/[.!?。！？、]/.test(fullText[startIndex - 1]) || startIndex === 1) {
        break;
      }
      startIndex--;
    }
    
    // If we found sentence-ending punctuation, adjust startIndex 
    if (startIndex > 0 && /[.!?。！？、]/.test(fullText[startIndex - 1])) {
      startIndex = startIndex;
    } else if (startIndex === 0) {
      startIndex = 0;
    }
    
    // Find the end of the sentence
    let endIndex = targetIndex + targetWord.length;
    while (endIndex < fullText.length) {
      // Look for sentence-ending punctuation
      if (/[.!?。！？、]/.test(fullText[endIndex])) {
        endIndex++; // Include the punctuation
        break;
      }
      endIndex++;
    }
    
    // Get the complete sentence
    return fullText.substring(startIndex, endIndex).trim();
  } catch (err) {
    logger.error(`ChatbotChineseLookup - Error extracting context for "${targetWord}":`, err);
    return fullText;
  }
};

// Retry mechanism for API calls
const MAX_RETRIES = 2;
const RETRY_DELAY = 1000;

const ChatbotChineseLookup = ({ word, sentence, onSelectWord, children }) => {
  const [data, setData] = useState(null);
  const [hanziDetailsData, setHanziDetailsData] = useState(null);
  const [contextData, setContextData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openAccordions, setOpenAccordions] = useState({
    context: false,
    grammar: false
  });
  const [openHanziAccordions, setOpenHanziAccordions] = useState({});
  const [wordGrade, setWordGrade] = useState(1);
  const [wordKnown, setWordKnown] = useState(false);
  const [hanziGrades, setHanziGrades] = useState({});
  const [hanziKnownStatus, setHanziKnownStatus] = useState({});
  const [justSavedWord, setJustSavedWord] = useState(false);
  const [justSavedHanzi, setJustSavedHanzi] = useState('');
  const [apiFailure, setApiFailure] = useState(false);
  const [showWordGrading, setShowWordGrading] = useState(false);
  const [showHanziGrading, setShowHanziGrading] = useState({});
  const [hasBeenClicked, setHasBeenClicked] = useState(false);

  // Log parameters for debugging
  logger.debug(`ChatbotChineseLookup initialized with word: "${word}", sentence: "${sentence || 'none'}"`);

  // Use the context for saved words
  const {
    isWordSaved,
    isHanziSaved,
    addSavedWord,
    removeSavedWord,
    addSavedHanzi,
    removeSavedHanzi,
    getWordGrade,
    isWordKnown,
    getHanziGrade,
    isHanziKnown,
    updateWordGrade,
    updateWordKnown,
    updateHanziGrade,
    updateHanziKnown,
    refreshSavedWords
  } = useWordContext();

  // Get saved status from context
  const isSaved = isWordSaved(word, 'Chinese') || justSavedWord;
  
  // Extract hanzi characters from the word
  const hanziChars = Array.from(word)
    .filter(char => /[\u4E00-\u9FFF]/.test(char));
  
  // Get saved hanzi list
  const savedHanziList = hanziChars
    .filter(char => isHanziSaved(char) || justSavedHanzi === char);

  // Initialize hanzi grades and known status
  useEffect(() => {
    const newGrades = {};
    const newKnownStatus = {};
    const newShowGrading = {};

    hanziChars.forEach(char => {
      if (isHanziSaved(char) || justSavedHanzi === char) {
          newGrades[char] = getHanziGrade(char);
          newKnownStatus[char] = isHanziKnown(char);
        newShowGrading[char] = true;
        } else {
          newGrades[char] = 1;
          newKnownStatus[char] = false;
        newShowGrading[char] = false;
        }
      });

    setHanziGrades(newGrades);
    setHanziKnownStatus(newKnownStatus);
    setShowHanziGrading(newShowGrading);
    logger.debug('ChatbotChineseLookup - Updated hanzi grades:', newGrades);
    
    // Show grading UI if word is saved
    setShowWordGrading(isSaved);
  }, [word, isHanziSaved, getHanziGrade, isHanziKnown, justSavedHanzi, isSaved]);

  // Update local state when the word's saved status changes
  useEffect(() => {
    if (isSaved) {
      setWordGrade(getWordGrade(word, 'Chinese'));
      setWordKnown(isWordKnown(word, 'Chinese'));
      setShowWordGrading(true);
      logger.debug(`ChatbotChineseLookup - Word "${word}" is saved with grade:`, getWordGrade(word, 'Chinese'));
    } else {
      setWordGrade(1);
      setWordKnown(false);
      setShowWordGrading(false);
    }
  }, [isSaved, word, getWordGrade, isWordKnown]);

  // Reset state when word changes
  useEffect(() => {
    setData(null);
    setHanziDetailsData(null);
    setContextData(null);
    setError(null);
    setOpenAccordions({
      context: false,
      grammar: false
    });
    setOpenHanziAccordions({});
    setHasBeenClicked(false);
    
    // If in dictionary panel mode, auto-fetch data
    if (onSelectWord === null && word) {
      setHasBeenClicked(true);
      fetchData();
      logger.debug(`ChatbotChineseLookup - Auto-fetching data for panel display, word: "${word}"`);
    }
  }, [word]); // Only run when word changes

  // Helper function to get userId
  const getUserId = () => {
    // Try to get from JWT token first
    const token = localStorage.getItem('access_token');
    if (token) {
      try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map((c) => {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));

        const decodedToken = JSON.parse(jsonPayload);
        if (decodedToken.sub) {
          const userId = decodedToken.sub;
          logger.debug(`ChatbotChineseLookup - Extracted user ID from token: ${userId}`);
          localStorage.setItem('userId', userId);
          return userId;
        }
      } catch (err) {
        logger.error("ChatbotChineseLookup - Error decoding token:", err);
      }
    }

    // Fall back to localStorage
    const localUserId = localStorage.getItem('userId');
    if (localUserId) {
      logger.debug("ChatbotChineseLookup - Using userId from localStorage (fallback)");
      return localUserId;
    }

    logger.warn("ChatbotChineseLookup - No userId available from token or localStorage");
    return null;
  };

  // Helper function for API call retries
  const fetchWithRetry = async (apiCall, failureMessage, maxRetries = MAX_RETRIES) => {
    let retries = 0;
    
    while (retries <= maxRetries) {
      try {
        logger.debug(`ChatbotChineseLookup - Retry ${retries}/${maxRetries} for API call`);
        const response = await apiCall();
        return response;
      } catch (err) {
        retries++;
        
        logger.error(`API call failed (attempt ${retries}/${maxRetries + 1}):`, err);
        
        if (retries > maxRetries) {
          logger.error(`All ${maxRetries + 1} attempts failed for API call. Last error:`, err);
          throw err;
        }
        
        // Wait between retries with exponential backoff
        const delay = RETRY_DELAY * Math.pow(2, retries - 1);
        logger.debug(`ChatbotChineseLookup - Waiting ${delay}ms before retry`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  };

  // Main data fetching function
  const fetchData = async () => {
    if (loading) return; // Prevent duplicate requests
    
    setLoading(true);
    setError(null);
    setApiFailure(false);
    
    // Track if we've found a definition to avoid unnecessary further lookups
    let definitionFound = false;
    
    try {
      logger.debug(`ChatbotChineseLookup - Fetching data for word: "${word}"`);
      
      // 1. Fetch word definition from API with Gemini integration
      // First try to get the contextual definition if a sentence is provided
      if (sentence) {
        try {
          logger.debug(`ChatbotChineseLookup - Fetching context-based Gemini definition for "${word}" in sentence: "${sentence}"`);
          
          // Use smaller sentence context to avoid exceeding token limits
          const contextSentence = extractSentenceContext(sentence, word);
          logger.debug(`ChatbotChineseLookup - Using extracted context: "${contextSentence}"`);
          
          const contextResponse = await fetchWithRetry(
            () => apiClient.get(`/api/contextDefine?word=${encodeURIComponent(word)}&sentence=${encodeURIComponent(contextSentence)}&lang=zh`),
            "Failed to get Gemini definition"
          );
          
          const contextResult = await contextResponse.json();
          logger.debug("ChatbotChineseLookup - Received Gemini context definition:", contextResult);
          
          if (contextResult && contextResult.definitions && contextResult.definitions.length > 0) {
            // Set data from Gemini response
            setData({
              word: word,
              pinyin: "...", // Will be updated from hanzi details
              definitions: contextResult.definitions
            });
            
            // Also set context and grammar data from the same response
            setContextData({ 
              context_note: contextResult.context_note || "No context note available.",
              grammar_note: contextResult.grammar_note || "Grammar information unavailable."
            });
            
            logger.debug("ChatbotChineseLookup - Context note from Gemini received");
            definitionFound = true;
          }
        } catch (geminiErr) {
          logger.error("ChatbotChineseLookup - Error fetching from Gemini context definition:", geminiErr);
          // Fall back to traditional dictionary lookup below
        }
      }
      
      // If no sentence or context definition failed, try regular definition
      if (!definitionFound && !sentence) {
        try {
          logger.debug(`ChatbotChineseLookup - Fetching Gemini definition for "${word}"`);
          
          const defineResponse = await fetchWithRetry(
            () => apiClient.get(`/api/define?word=${encodeURIComponent(word)}&lang=zh`),
            "Failed to get Gemini definition"
          );
          
          const defineResult = await defineResponse.json();
          logger.debug("ChatbotChineseLookup - Received Gemini definition:", defineResult);
          
          if (defineResult && defineResult.definitions && defineResult.definitions.length > 0) {
            // Set data from Gemini response
            setData({
              word: word,
              pinyin: "...", // Will be updated from hanzi details
              definitions: defineResult.definitions
            });
            definitionFound = true;
          }
        } catch (geminiErr) {
          logger.error("ChatbotChineseLookup - Error fetching from Gemini definition:", geminiErr);
          // Fall back to traditional dictionary lookup below
        }
      }
      
      // 2. Fall back to traditional CEDICT dictionary lookup as before
      // If we still don't have definitions from Gemini, try the Chinese dictionary
      if (!definitionFound) {
        try {
          logger.debug(`ChatbotChineseLookup - Falling back to traditional Chinese dictionary for "${word}"`);
          
          const wordResponse = await fetchWithRetry(
            () => apiClient.get(`/api/chinese-definition?keyword=${encodeURIComponent(word)}`),
            "Failed to get Chinese definition"
          );
          
          // Parse the JSON response
          const cedictResults = await wordResponse.json();
          
          if (cedictResults && cedictResults.length > 0) {
            const firstMatch = cedictResults[0];
            setData({
              word: word,
              pinyin: firstMatch.pinyin,
              definitions: firstMatch.definitions
            });
            definitionFound = true;
            logger.debug(`ChatbotChineseLookup - Definition data received for "${word}"`, firstMatch);
          }
        } catch (err) {
          logger.error("ChatbotChineseLookup - Error fetching definition from CEDICT:", err);
          // Will handle this in the individual character fallback below
        }
      }
      
      // 3. If word lookup failed and it's more than one character, try individual characters
      if (!definitionFound && word.length > 1) {
        logger.debug(`ChatbotChineseLookup - Trying individual characters for "${word}"`);
        
        const individualResults = [];
        
        // Try each character individually
        for (const char of Array.from(word)) {
          if (/[\u4E00-\u9FFF]/.test(char)) { // Only try Chinese characters
            try {
              const charResponse = await apiClient.get(`/api/chinese-definition?keyword=${encodeURIComponent(char)}`);
              const charData = await charResponse.json();
              
              if (charData && charData.length > 0) {
                individualResults.push({
                  char,
                  pinyin: charData[0].pinyin,
                  def: charData[0].definitions[0] // Just take first definition
                });
              }
            } catch (err) {
              logger.error(`ChatbotChineseLookup - Error fetching for char "${char}":`, err);
            }
          }
        }
        
        // If we found any individual character definitions
        if (individualResults.length > 0) {
          const combinedPinyin = individualResults.map(r => r.pinyin).join(' ');
          const combinedDef = [
            `Compound word with components:`, 
            ...individualResults.map(r => `• ${r.char}: ${r.pinyin} - ${r.def}`)
          ];
          
          setData({
            word: word,
            pinyin: combinedPinyin,
            definitions: combinedDef
          });
          definitionFound = true;
          logger.debug(`ChatbotChineseLookup - Using individual character definitions for "${word}"`);
        }
      }
      
      // 4. If we still didn't find a definition
      if (!definitionFound) {
        setData({ 
          word: word, 
          pinyin: "N/A", 
          definitions: ["Definition not found."] 
        });
        logger.debug(`ChatbotChineseLookup - No definition found for "${word}"`);
      }

      // 5. Fetch Hanzi details for individual characters
      if (hanziChars.length > 0) {
        try {
          logger.debug(`ChatbotChineseLookup - Fetching details for Hanzi: ${hanziChars.join(', ')}`);
          
          const hanziResponse = await fetchWithRetry(
            () => apiClient.post(`/api/hanzi-details`, { characters: hanziChars }),
            "Failed to get Hanzi details"
          );
          
          setHanziDetailsData(hanziResponse);
          logger.debug("ChatbotChineseLookup - Hanzi details received:", hanziResponse);
          
          // Update pinyin from hanzi details if available
          if (hanziResponse && Object.keys(hanziResponse).length > 0) {
            // Get the pinyin values for each character
            const pinyinArray = hanziChars.map(char => 
              hanziResponse[char] && hanziResponse[char].pinyin ? hanziResponse[char].pinyin : ''
            ).filter(p => p);
            
            // Update state if we have pinyin data
            if (pinyinArray.length > 0) {
              setData(prevData => {
                // Only update if we have existing data
                if (!prevData) return prevData;
                
                return {
                  ...prevData,
                  pinyin: pinyinArray.join(' ')
                };
              });
              logger.debug(`ChatbotChineseLookup - Updated pinyin to: ${pinyinArray.join(' ')}`);
            }
          }
        } catch (hanziErr) {
          logger.error("ChatbotChineseLookup - Error fetching Hanzi details:", hanziErr);
          setHanziDetailsData({});
        }
      }

      // 6. Fetch context/grammar notes if not already received from Gemini
      if (sentence && (!contextData || !contextData.context_note || contextData.context_note === "No context note available.")) {
        try {
          logger.debug(`ChatbotChineseLookup - Fetching context for word "${word}" in sentence: "${sentence}"`);
          
          // Use extracted context sentence for context note
          const contextSentence = extractSentenceContext(sentence, word);
          
          const contextResponse = await fetchWithRetry(
            () => apiClient.get(`/api/context-note-chinese?word=${encodeURIComponent(word)}&sentence=${encodeURIComponent(contextSentence)}`),
            "Failed to get context note"
          );
          
          const contextResult = await contextResponse.json();
          
          // Only set if we don't already have good context data
          if (!contextData || contextData.context_note === "No context note available.") {
            setContextData({ 
              context_note: contextResult.note || "No context note available.",
              grammar_note: contextResult.grammar_note || "Grammar information unavailable."
            });
            
            logger.debug("ChatbotChineseLookup - Context note received:", contextResult);
          }
        } catch (ctxErr) {
          logger.error("ChatbotChineseLookup - Error fetching context:", ctxErr);
          if (!contextData) {
            setContextData({ 
              context_note: "Error fetching context.",
              grammar_note: "Error fetching grammar information."
            });
          }
        }
      }
    } catch (err) {
      logger.error("ChatbotChineseLookup - Error fetching data:", err);
      setError("Failed to fetch details. Please try again later.");
      setApiFailure(true);
    } finally {
      setLoading(false);
    }
  };

  // Handle Click function to pass word to parent
  const handleClick = (e) => {
    e.stopPropagation();
    
    // If this is the first click, fetch data
    if (!hasBeenClicked) {
      setHasBeenClicked(true);
      fetchData(); // Only fetch data when clicked
    }
    
    if (onSelectWord && typeof onSelectWord === 'function') {
      // Get context for this specific word
      const wordContext = sentence ? extractSentenceContext(sentence, word) : word;
      onSelectWord(word, wordContext);
    }
  };

  // Toggle accordions
  const toggleAccordion = (section) => {
    setOpenAccordions(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Toggle hanzi accordion open/close state
  const toggleHanziAccordion = (hanziChar) => {
    // Close all accordions and only open the selected one
    const newOpenHanziAccordions = {};
    
    // If the clicked accordion was already open, close all of them
    // Otherwise, only open the clicked one
    if (openHanziAccordions[hanziChar]) {
      // Already open, so close all
      setOpenHanziAccordions({});
    } else {
      // Open only the clicked one
      newOpenHanziAccordions[hanziChar] = true;
      setOpenHanziAccordions(newOpenHanziAccordions);
    }
  };

  // Word saving/grading functions
  const handleSaveWord = async () => {
    const userId = getUserId();
    if (!userId) return;
    
    try {
      const result = await apiClient.post('/api/saveUserWord', {
        userId,
        item: word,
        language: 'Chinese',
        grade: 1,
        known: false
      });
      
      if (result && result.message) {
        setJustSavedWord(true);
        setWordGrade(1);
        setWordKnown(false);
        setShowWordGrading(true);
        refreshSavedWords();
        logger.debug(`ChatbotChineseLookup - Word "${word}" saved successfully`);
      }
    } catch (error) {
      logger.error(`ChatbotChineseLookup - Error saving word ${word}:`, error);
      setError("Failed to save word.");
    }
  };

  const handleDeleteWord = async () => {
    const userId = getUserId();
    if (!userId) return;
    
    try {
      const result = await apiClient.post('/api/removeUserWord', {
        userId,
        item: word,
        language: 'Chinese'
      });
      
      if (result && result.message) {
        setJustSavedWord(false);
        setShowWordGrading(false);
        refreshSavedWords();
        logger.debug(`ChatbotChineseLookup - Word "${word}" deleted successfully`);
      }
    } catch (error) {
      logger.error(`ChatbotChineseLookup - Error deleting word ${word}:`, error);
      setError("Failed to delete word.");
    }
  };

  // Word grading functions
  const handleWordGradeChange = async (e) => {
    const newGrade = parseInt(e.target.value, 10);
    setWordGrade(newGrade);
    
    if (isSaved) {
      await updateWordGrade(word, 'Chinese', newGrade);
    }
  };

  const handleWordKnownChange = async (e) => {
    const isKnown = e.target.checked;
    setWordKnown(isKnown);
    
    if (isSaved) {
      await updateWordKnown(word, 'Chinese', isKnown);
    }
  };

  // Handle saving a hanzi character
  const handleSaveHanzi = async (hanziChar) => {
    const userId = getUserId();
    
    if (!userId) {
      logger.error("ChatbotChineseLookup - No user ID available, cannot save hanzi");
      setError("Unable to save hanzi - user not logged in");
      return;
    }
    
    try {
      logger.debug(`ChatbotChineseLookup - Saving hanzi "${hanziChar}" with userId ${userId}`);
      
      // Call API to save
      const result = await apiClient.post('/api/saveUserHanzi', {
        userId,
        hanzi: hanziChar,
        language: 'Chinese',
        grade: hanziGrades[hanziChar] || 1,
        known: hanziKnownStatus[hanziChar] || false
      });
      
      // If successful, update the context - check for message property which indicates success
      if (result && (result.success || result.message)) {
        setJustSavedHanzi(hanziChar);
        addSavedHanzi(hanziChar, hanziGrades[hanziChar] || 1, hanziKnownStatus[hanziChar] || false);
        setShowHanziGrading(prev => ({ ...prev, [hanziChar]: true }));
        logger.debug(`ChatbotChineseLookup - Successfully saved hanzi "${hanziChar}"`);
        
        // Force refresh to update UI
        refreshSavedWords();
      } else {
        logger.error(`ChatbotChineseLookup - Failed to save hanzi "${hanziChar}": No success message in response`, result);
        setError("Error saving hanzi - please try again");
      }
    } catch (err) {
      logger.error(`ChatbotChineseLookup - Error saving hanzi "${hanziChar}":`, err);
      setError(`Error saving hanzi: ${err.message || 'Unknown error'}`);
    }
  };

  // Handle deleting a hanzi character
  const handleDeleteHanzi = async (hanziChar) => {
    const userId = getUserId();
    if (!userId) {
      setError("User not logged in. Cannot delete Hanzi.");
      logger.warn("ChatbotChineseLookup - User ID not found for Hanzi deletion.");
      return;
    }

    try {
      logger.debug(`ChatbotChineseLookup - Attempting API call to delete hanzi "${hanziChar}" for userId ${userId}`);
      
      // Call API to delete - We expect this might return an error if already deleted, or success
      await apiClient.post('/api/deleteUserHanzi', {
        userId,
        hanzi: hanziChar
      });
      
      // Regardless of API response (success or 404 on second attempt), update the context state
      // This ensures the UI reflects the deletion even if the API call fails because it was already deleted.
      logger.debug(`ChatbotChineseLookup - API call completed (or failed expectedly). Updating context state for "${hanziChar}"`);
      setJustSavedHanzi(''); // Clear any 'just saved' flag
      removeSavedHanzi(hanziChar); // Call the context function to update local state
      setShowHanziGrading(prev => ({ ...prev, [hanziChar]: false })); // Hide grading UI
      
      // Optionally trigger a refresh to ensure consistency, though direct context update might be enough
      // refreshSavedWords(); // Consider if this is still needed after direct context update
      
    } catch (err) {
      // Log unexpected errors (e.g., network issues, server errors other than 404)
      logger.error(`ChatbotChineseLookup - Unexpected error during Hanzi deletion API call for "${hanziChar}":`, err);
      // We might still want to update the UI state even on unexpected errors, 
      // assuming the goal is to remove it from view. Or show an error message.
      // For now, we'll update the state anyway, as the user's intent was to delete.
      logger.warn(`ChatbotChineseLookup - Proceeding with context state update for "${hanziChar}" despite API error.`);
      setJustSavedHanzi('');
      removeSavedHanzi(hanziChar);
      setShowHanziGrading(prev => ({ ...prev, [hanziChar]: false }));
      
      // Maybe show a less critical error message to the user
      // setError(`Could not confirm Hanzi deletion with server, but removed from view.`);
    }
  };

  // Hanzi grading functions
  const handleHanziGradeChange = async (hanziChar, e) => {
    const newGrade = parseInt(e.target.value, 10);
    setHanziGrades(prev => ({ ...prev, [hanziChar]: newGrade }));
    
    if (isHanziSaved(hanziChar) || justSavedHanzi === hanziChar) {
      await updateHanziGrade(hanziChar, newGrade);
      // Force refresh of saved words to update highlighting immediately
      refreshSavedWords();
    }
  };

  const handleHanziKnownChange = async (hanziChar, e) => {
    const isKnown = e.target.checked;
    setHanziKnownStatus(prev => ({ ...prev, [hanziChar]: isKnown }));
    
    if (isHanziSaved(hanziChar) || justSavedHanzi === hanziChar) {
      await updateHanziKnown(hanziChar, isKnown);
      // Force refresh of saved words to update highlighting immediately
      refreshSavedWords();
    }
  };

  // Render word grading UI
  const renderWordGradingUI = () => {
    if (!showWordGrading) return null;
    
    return (
      <div className="chatbot-grading">
        <div className="difficulty-scale">
          <div className="difficulty-labels">
            <span className="difficulty-hard">Hard</span>
            <span className="difficulty-easy">Easy</span>
          </div>
          <div className="radio-group">
            {[1, 2, 3, 4, 5].map(grade => (
              <label key={`word-grade-${grade}`} className={`radio-option radio-grade-${grade} ${wordGrade === grade ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="word-grade"
                  value={grade}
                  checked={wordGrade === grade}
                  onChange={handleWordGradeChange}
                />
                <span>{grade}</span>
              </label>
            ))}
          </div>
        </div>
        
        <div className="known-checkbox">
          <input
            type="checkbox"
            id="word-known"
            checked={wordKnown}
            onChange={handleWordKnownChange}
          />
          <div className="known-checkbox-info">
            <label htmlFor="word-known" className="known-checkbox-label">
              I know this word
            </label>
            <div className="known-checkbox-description">
              Words marked as known won't be highlighted in texts
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render hanzi grading UI
  const renderHanziGradingUI = (hanziChar) => {
    if (!showHanziGrading[hanziChar]) return null;
    
    return (
      <div className="chatbot-grading">
        <div className="difficulty-scale">
          <div className="difficulty-labels">
            <span className="difficulty-hard">Hard</span>
            <span className="difficulty-easy">Easy</span>
          </div>
          <div className="radio-group">
            {[1, 2, 3, 4, 5].map(grade => (
              <label key={`hanzi-grade-${hanziChar}-${grade}`} className={`radio-option radio-grade-${grade} ${hanziGrades[hanziChar] === grade ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name={`hanzi-grade-${hanziChar}`}
                  value={grade}
                  checked={hanziGrades[hanziChar] === grade}
                  onChange={(e) => handleHanziGradeChange(hanziChar, e)}
                />
                <span>{grade}</span>
              </label>
            ))}
          </div>
        </div>
        
        <div className="known-checkbox">
          <input
            type="checkbox"
            id={`hanzi-known-${hanziChar}`}
            checked={hanziKnownStatus[hanziChar] || false}
            onChange={(e) => handleHanziKnownChange(hanziChar, e)}
          />
          <div className="known-checkbox-info">
            <label htmlFor={`hanzi-known-${hanziChar}`} className="known-checkbox-label">
              I know this Hanzi
            </label>
            <div className="known-checkbox-description">
              Hanzi marked as known won't be highlighted in texts
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render Hanzi details
  const renderHanziInfo = (hanziChar, details) => {
    const isCharSaved = isHanziSaved(hanziChar) || justSavedHanzi === hanziChar;
    
    return (
      <div className="chatbot-kanji-info">
        {details ? (
          <>
            {details.definition && (
              <div className="chatbot-kanji-meaning">
                <span className="chatbot-definition-label">Meaning:</span>
                <span>{details.definition}</span>
              </div>
            )}
            {details.pinyin && (
              <div className="chatbot-kanji-meaning">
                <span className="chatbot-definition-label">Reading:</span>
                <span>{details.pinyin}</span>
              </div>
            )}
            {details.hsk_level && (
              <div className="chatbot-kanji-meaning">
                <span className="chatbot-definition-label">HSK Level:</span>
                <span>{details.hsk_level}</span>
              </div>
            )}
            {details.frequency_rank && (
              <div className="chatbot-kanji-meaning">
                <span className="chatbot-definition-label">Frequency:</span>
                <span>{details.frequency_rank}</span>
              </div>
            )}
          </>
        ) : (
          <div className="chatbot-kanji-meaning">Details not available for this Hanzi.</div>
        )}
        
        <div className="chatbot-word-buttons">
          {!isCharSaved ? (
            <button 
              className="chatbot-save-btn"
              onClick={() => handleSaveHanzi(hanziChar)}
            >
              Save Hanzi
            </button>
          ) : (
            <button 
              className="chatbot-delete-btn"
              onClick={() => handleDeleteHanzi(hanziChar)}
            >
              Delete Hanzi
            </button>
          )}
        </div>
        
        {renderHanziGradingUI(hanziChar)}
      </div>
    );
  };

  // Main dictionary content render
  const renderDictionaryContent = () => {
    if (loading && !data) {
      return <div className="chatbot-loading">Loading...</div>;
    }
    
    if (error) {
      return (
        <div className="chatbot-error-container">
          <div className="chatbot-error-text">{error}</div>
          <div className="chatbot-error-note">
            There was a problem loading the dictionary information. Please try again later.
          </div>
        </div>
      );
    }
    
    if (!data) {
      return <div className="chatbot-no-data-message">No data found.</div>;
    }
    
  return (
      <div className="chatbot-dictionary-word-definition">
        {/* Word header */}
        <div className="chatbot-dictionary-header">
              <h3 className="chatbot-word-heading">{data.word}</h3>
          {data.pinyin && (
            <span className="chatbot-reading">{data.pinyin}</span>
          )}
        </div>
        
        {/* Word definitions */}
        <div className="chatbot-definition-content">
          {data.definitions && data.definitions.map((def, index) => (
            <div key={index} className="chatbot-definition-item">
              {def}
            </div>
          ))}
        </div>
        
        {/* Word save/delete buttons */}
        <div className="chatbot-word-buttons">
          {!isSaved ? (
            <button 
              className="chatbot-save-btn"
              onClick={handleSaveWord}
            >
              Save Word
            </button>
          ) : (
            <button 
              className="chatbot-delete-btn"
              onClick={handleDeleteWord}
            >
              Delete Word
            </button>
          )}
        </div>
        
        {/* Word grading UI */}
        {renderWordGradingUI()}
        
        {/* Context section - show accordion only if sentence is provided */}
        {sentence && contextData && (
          <div className="chatbot-definition-accordion">
            <div 
              className="chatbot-definition-accordion-header"
              onClick={() => toggleAccordion('context')}
            >
              <h4 className="chatbot-definition-accordion-title">Context</h4>
              <span className="chatbot-definition-accordion-icon">
                {openAccordions.context ? '▼' : '▶'}
              </span>
            </div>
            
            {openAccordions.context && (
              <div className="chatbot-definition-accordion-content">
                <p className="chatbot-context-note">{contextData.context_note}</p>
              </div>
            )}
          </div>
        )}

        {/* Grammar section - show accordion only if sentence is provided */}
        {sentence && contextData && contextData.grammar_note && (
          <div className="chatbot-definition-accordion">
            <div 
              className="chatbot-definition-accordion-header"
              onClick={() => toggleAccordion('grammar')}
            >
              <h4 className="chatbot-definition-accordion-title">Grammar</h4>
              <span className="chatbot-definition-accordion-icon">
                {openAccordions.grammar ? '▼' : '▶'}
              </span>
            </div>
            
            {openAccordions.grammar && (
              <div className="chatbot-definition-accordion-content">
                <p className="chatbot-grammar-note">{contextData.grammar_note}</p>
              </div>
            )}
          </div>
        )}
        
        {/* Hanzi components section */}
        {hanziChars.length > 0 && (
          <div className="chatbot-kanji-components-section">
            <h4 className="chatbot-kanji-components-title">Hanzi Components</h4>
            
            {hanziChars.map((hanziChar, index) => (
              <div key={`hanzi-${index}`} className="chatbot-kanji-item">
                <div 
                  className="chatbot-definition-accordion-header"
                  onClick={() => toggleHanziAccordion(hanziChar)}
                >
                        <span className="chatbot-clickable-kanji">{hanziChar}</span>
                  <span className="chatbot-definition-accordion-icon">
                    {openHanziAccordions[hanziChar] ? '▼' : '▶'}
    </span>
                </div>
                
                {openHanziAccordions[hanziChar] && (
                  <div className="chatbot-definition-accordion-content">
                    {renderHanziInfo(hanziChar, hanziDetailsData ? hanziDetailsData[hanziChar] : null)}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };
  
  // Helper function to get the CSS class for the word
  function getWordClass() {
    if (!isSaved) {
      return 'chatbot-clickable-word';
    }
    
    if (wordKnown) {
      return 'chatbot-clickable-word'; // No highlight if known
    }
    
    return `chatbot-clickable-word chatbot-saved-word chatbot-grade-${wordGrade}`;
  }

  // Helper function to get CSS class for Hanzi
  function getHanziClass(hanziChar) {
    const isCharSaved = isHanziSaved(hanziChar) || justSavedHanzi === hanziChar;
    
    if (!isCharSaved) {
      return 'chatbot-clickable-hanzi';
    }
    
    if (hanziKnownStatus[hanziChar]) {
      return 'chatbot-clickable-hanzi';
    }
    
    return `chatbot-clickable-hanzi chatbot-saved-hanzi chatbot-grade-${hanziGrades[hanziChar] || 1}`;
  }

  // Return the component JSX
  return onSelectWord === null ? (
    // In dictionary panel mode, render only the content
    renderDictionaryContent()
  ) : (
    // In clickable word mode, render as a span
    <span 
      className={getWordClass()}
      onClick={handleClick}
    >
      {children || Array.from(word).map((char, charIndex) => {
        if (/[\u4E00-\u9FFF]/.test(char)) {
          return (
            <span
              key={`hanzi-${charIndex}`}
              className={getHanziClass(char)}
            >
              {char}
            </span>
          );
        }
        return char;
      })}
    </span>
  );
};

export default ChatbotChineseLookup; 